import { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { portalApi, type PaymentAccount, type SettlementBatch, type Withdrawal, type WithdrawSummary } from '@/api/portal';
import { formatPlatformTime, PLATFORM_TIME_ZONE_LABEL } from '@/utils/platformTime';

type WithdrawCurrency = 'CNY' | 'USDT';

function formatMoney(value = 0, currency: WithdrawCurrency = 'CNY') {
  const symbol = currency === 'USDT' ? '₮' : '¥';
  return `${symbol}${(value / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatUsdtMinor(value = 0) {
  return `${(value / 1_000_000).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 6 })} USDT`;
}

function formatUsdtPrice(value = 0) {
  if (value <= 0) return '-';
  return `¥${(value / 1_000_000).toLocaleString('zh-CN', { minimumFractionDigits: 4, maximumFractionDigits: 6 })}`;
}

export default function WithdrawPage() {
  const [currency, setCurrency] = useState<WithdrawCurrency>('CNY');
  const [amount, setAmount] = useState('');
  const [accounts, setAccounts] = useState<PaymentAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<number | null>(null);
  const [remark, setRemark] = useState('');
  const [summary, setSummary] = useState<WithdrawSummary | null>(null);
  const [history, setHistory] = useState<Withdrawal[]>([]);
  const [settlements, setSettlements] = useState<SettlementBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [lastWithdrawalNo, setLastWithdrawalNo] = useState('');

  const targetAccountType = currency === 'USDT' ? 'usdt' : 'bank';
  const availableBalance = summary?.availableAmount || 0;
  const minAmount = summary?.minAmount || (currency === 'USDT' ? 1000 : 100);
  const maxAmount = summary?.maxAmount || (currency === 'USDT' ? 10000000 : 50000000);
  const minFee = summary?.minFee ?? (currency === 'USDT' ? 300 : 0);
  const feeRate = summary?.feeRate ?? 0;
  const amountValue = Math.round((parseFloat(amount) || 0) * 100);
  const fee = amountValue > 0 ? (feeRate === 0 ? minFee : Math.max(minFee, Math.ceil(amountValue * (feeRate / 10000)))) : 0;
  const actual = Math.max(amountValue - fee, 0);

  const visibleAccounts = useMemo(
    () => accounts.filter((item) => item.type === targetAccountType && item.status !== 'disabled'),
    [accounts, targetAccountType]
  );

  useEffect(() => {
    loadData(currency);
  }, [currency]);

  async function loadData(nextCurrency: WithdrawCurrency) {
    setLoading(true);
    setError('');
    const [summaryRes, accountsRes, withdrawalsRes, settlementsRes] = await Promise.allSettled([
      portalApi.withdrawSummary(nextCurrency),
      portalApi.paymentAccounts(),
      portalApi.withdrawals({ pageNum: 1, pageSize: 20, currency: nextCurrency }),
      portalApi.settlements({ pageNum: 1, pageSize: 20, currency: nextCurrency }),
    ]);

    if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value);
    else setError(summaryRes.reason?.message || '加载提现摘要失败');

    if (accountsRes.status === 'fulfilled') {
      const list = accountsRes.value.accountList || [];
      setAccounts(list);
      const accountType = nextCurrency === 'USDT' ? 'usdt' : 'bank';
      const candidates = list.filter((item) => item.type === accountType && item.status !== 'disabled');
      const defaultAccount = candidates.find((item) => Boolean(item.isDefault)) || candidates[0];
      setSelectedAccount(defaultAccount?.id ?? null);
    } else {
      setAccounts([]);
      setSelectedAccount(null);
      setError(accountsRes.reason?.message || '加载到账账号失败');
    }

    if (withdrawalsRes.status === 'fulfilled') setHistory(withdrawalsRes.value.withdrawalList || []);
    else if (summaryRes.status === 'fulfilled' && accountsRes.status === 'fulfilled') setError(withdrawalsRes.reason?.message || '加载提现记录失败');

    if (settlementsRes.status === 'fulfilled') setSettlements(settlementsRes.value.settlementList || []);
    else {
      setSettlements([]);
      if (summaryRes.status === 'fulfilled' && accountsRes.status === 'fulfilled' && withdrawalsRes.status === 'fulfilled') {
        setError(settlementsRes.reason?.message || '加载后台结算记录失败');
      }
    }

    setLoading(false);
  }

  function switchCurrency(next: WithdrawCurrency) {
    setCurrency(next);
    setAmount('');
    setRemark('');
    setSubmitted(false);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (amountValue <= 0 || !selectedAccount) return;
    if (amountValue > availableBalance || amountValue < minAmount || amountValue > maxAmount) return;

    setSubmitting(true);
    setError('');
    try {
      const res = await portalApi.createWithdrawal({ accountId: selectedAccount, amount: amountValue, currency, remark });
      setLastWithdrawalNo(res.withdrawalNo);
      setSubmitted(true);
      setAmount('');
      setRemark('');
      await loadData(currency);
    } catch (err: any) {
      setError(err.message || '提交提现申请失败');
    } finally {
      setSubmitting(false);
    }
  };

  function statusBadge(status: string) {
    const map: Record<string, { cls: string; label: string }> = {
      pending: { cls: 'tech-badge-warning', label: '待处理' },
      processing: { cls: 'tech-badge-warning', label: '处理中' },
      completed: { cls: 'tech-badge-success', label: '已到账' },
      failed: { cls: 'tech-badge-error', label: '失败' },
      canceled: { cls: 'bg-slate-500/10 text-slate-400 border border-slate-500/20', label: '已取消' },
    };
    const s = map[status] ?? { cls: 'bg-slate-500/10 text-slate-400 border border-slate-500/20', label: status };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${s.cls}`}>{s.label}</span>;
  }

  function settlementStatusBadge(status: string) {
    const completed = status === 'completed';
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${completed ? 'tech-badge-success' : 'tech-badge-warning'}`}>
        {completed ? '已结算' : '待打款'}
      </span>
    );
  }

  function feeLabel(short = false) {
    if (feeRate > 0) {
      return `手续费率 ${(feeRate / 100).toFixed(2)}%，最低 ${formatMoney(minFee, currency)}${short ? '' : '/笔'}`;
    }
    if (minFee > 0) {
      return `${isUsdt ? '网络手续费' : '固定手续费'} ${formatMoney(minFee, currency)}${short ? '' : '/笔'}`;
    }
    return '无手续费';
  }

  const isUsdt = currency === 'USDT';

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {error && <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">{error}</div>}

        <div className="flex items-center gap-2">
          <CurrencyButton label="法币提现 CNY" icon="ri-money-cny-circle-line" active={!isUsdt} onClick={() => switchCurrency('CNY')} />
          <CurrencyButton label="USDT 提现" icon="ri-currency-line" active={isUsdt} amber onClick={() => switchCurrency('USDT')} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">
            <div className="tech-card p-5 flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 mb-1">{isUsdt ? '可用余额（USDT）' : '可用余额（法币）'}</p>
                <p className="text-2xl font-bold text-slate-100">
                  {loading ? '--' : formatMoney(availableBalance, currency)}
                  <span className="text-sm font-normal text-slate-500 ml-1">{currency}</span>
                </p>
                <p className="text-xs text-slate-500 mt-1">冻结 {formatMoney(summary?.frozenAmount || 0, currency)}，今日已提现 {summary?.todayCount || 0}/{summary?.dailyLimit || 5} 次</p>
              </div>
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl border ${isUsdt ? 'bg-amber-500/10 border-amber-500/20' : 'bg-blue-500/10 border-blue-500/20'}`}>
                <i className={`text-xl ${isUsdt ? 'ri-currency-line text-amber-400' : 'ri-wallet-3-line text-blue-400'}`} />
              </div>
            </div>

            <div className="tech-card p-5 md:p-6">
              <h3 className="text-base font-semibold text-slate-100 mb-5 flex items-center gap-2">
                <span className={`w-5 h-5 flex items-center justify-center ${isUsdt ? 'text-amber-400' : 'text-blue-400'}`}>
                  <i className={isUsdt ? 'ri-currency-line' : 'ri-money-cny-circle-line'} />
                </span>
                发起{isUsdt ? 'USDT' : '法币'}提现
              </h3>

              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-500/10 border border-green-500/20 mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-green-400" />
                  </div>
                  <h4 className="text-lg font-semibold text-slate-100 mb-2">提现申请已提交</h4>
                  <p className="text-sm text-slate-400 mb-1">提现单号：<span className="font-mono text-blue-300">{lastWithdrawalNo}</span></p>
                  <p className="text-sm text-slate-500">{isUsdt ? '链上确认预计 5-30 分钟' : '预计 1-2 个工作日到账'}</p>
                  <button onClick={() => { setSubmitted(false); setAmount(''); setRemark(''); }} className="tech-btn-secondary mt-6 inline-flex items-center gap-2 px-5 py-2 text-sm">
                    <i className="ri-arrow-go-back-line" />
                    再次提现
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">提现金额 <span className="text-xs text-slate-500">（单位：{currency}）</span></label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500 font-medium">{isUsdt ? '₮' : '¥'}</span>
                      <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`请输入提现金额（${currency}）`} min={minAmount / 100} max={Math.min(availableBalance, maxAmount) / 100} step={0.01} className="tech-input w-full pl-8 pr-3 py-2.5 text-sm" required />
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-slate-500">最低 {formatMoney(minAmount, currency)}，单笔最高 {formatMoney(maxAmount, currency)}</p>
                      <button type="button" onClick={() => setAmount((Math.min(availableBalance, maxAmount) / 100).toFixed(2))} className="text-xs text-blue-400 hover:text-blue-300">全部提现</button>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-slate-300">{isUsdt ? '到账钱包地址' : '到账银行卡'}</label>
                      <a href="#/payment-accounts" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                        <i className="ri-settings-3-line" />
                        管理账号
                      </a>
                    </div>
                    <div className="space-y-2">
                      {visibleAccounts.length === 0 && (
                        <div className="rounded-lg border border-dashed border-slate-700/70 bg-slate-800/30 p-4 text-sm text-slate-400">暂无可用{isUsdt ? 'USDT钱包' : '银行卡'}，请先添加提现账号</div>
                      )}
                      {visibleAccounts.map((account) => (
                        <label key={account.id} className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-all ${selectedAccount === account.id ? (isUsdt ? 'border-amber-500/40 bg-amber-500/5' : 'border-blue-500/40 bg-blue-500/5') : 'border-slate-700/60 bg-slate-800/40 hover:border-slate-600/60'}`}>
                          <input type="radio" name="account" value={account.id} checked={selectedAccount === account.id} onChange={() => setSelectedAccount(account.id)} className={`${isUsdt ? 'accent-amber-500' : 'accent-blue-500'} w-4 h-4`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-slate-200">{account.name}</span>
                              {isUsdt && account.extra && <span className="text-xs text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/20">{account.extra}</span>}
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5 font-mono truncate">{account.account}</p>
                            <p className="text-xs text-slate-500">{account.holder}{!isUsdt && account.extra ? ` · ${account.extra}` : ''}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {amountValue > 0 && (
                    <div className="rounded-lg border border-slate-700/60 bg-slate-800/40 p-4 space-y-2">
                      <PreviewLine label="提现金额" value={formatMoney(amountValue, currency)} />
                      <PreviewLine label={feeLabel(true)} value={`-${formatMoney(fee, currency)}`} />
                      <div className="border-t border-slate-700/50 pt-2 flex items-center justify-between text-sm">
                        <span className="text-slate-300 font-medium">实际到账</span>
                        <span className="text-green-400 font-bold text-base">{formatMoney(actual, currency)}</span>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">备注 <span className="text-xs text-slate-500">（选填）</span></label>
                    <input type="text" value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="例如：季度结算" maxLength={50} className="tech-input w-full px-3 py-2 text-sm" />
                  </div>

                  <button type="submit" disabled={submitting || loading || amountValue < minAmount || amountValue > availableBalance || amountValue > maxAmount || !selectedAccount} className="tech-btn-primary w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm disabled:opacity-50">
                    {submitting ? <><i className="ri-loader-4-line animate-spin" />提交中...</> : <><i className="ri-send-plane-line" />确认提现</>}
                  </button>
                </form>
              )}
            </div>
          </div>

          <div className="space-y-5">
            <InfoPanel title={isUsdt ? 'USDT 提现规则' : '提现规则'} icon="ri-information-line" amber={isUsdt}>
              {isUsdt ? (
                <>
                  <RuleLine amber label={`单笔最低 ${formatMoney(minAmount, currency)}，最高 ${formatMoney(maxAmount, currency)}`} />
                  <RuleLine amber label={feeLabel()} />
                  <RuleLine amber label="TRC20 到账预计 2-5 分钟" />
                  <RuleLine amber label="ERC20 到账预计 5-15 分钟" />
                  <RuleLine amber label="请务必确认钱包地址和链类型一致" />
                </>
              ) : (
                <>
                  <RuleLine label={`单笔最低 ${formatMoney(minAmount, currency)}，最高 ${formatMoney(maxAmount, currency)}`} />
                  <RuleLine label={feeLabel()} />
                  <RuleLine label="工作日 10:00 前申请，预计当日到账" />
                  <RuleLine label="非工作日申请，顺延至下一工作日处理" />
                  <RuleLine label={`每日最多提现 ${summary?.dailyLimit || 5} 次`} />
                </>
              )}
            </InfoPanel>

            <InfoPanel title={isUsdt ? '链上确认时效' : '处理时效'} icon="ri-time-line" amber>
              {isUsdt ? (
                <>
                  <TimeLine left="TRC20 网络" right="2-5 分钟" green />
                  <TimeLine left="ERC20 网络" right="5-15 分钟" />
                  <TimeLine left="BEP20 网络" right="3-8 分钟" green />
                </>
              ) : (
                <>
                  <TimeLine left="工作日 10:00 前" right="当日到账" green />
                  <TimeLine left="工作日 10:00 后" right="次日到账" />
                  <TimeLine left="周末 / 节假日" right="顺延至工作日" muted />
                </>
              )}
            </InfoPanel>
          </div>
        </div>

        <div className="tech-card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-700/60">
            <h3 className="text-base font-semibold text-slate-100 flex items-center gap-2">
              <span className={`w-5 h-5 flex items-center justify-center ${isUsdt ? 'text-amber-400' : 'text-emerald-400'}`}><i className="ri-exchange-dollar-line" /></span>
              后台结算记录
            </h3>
            <p className="mt-1 text-xs text-slate-500">后台按订单区间统一结算的批次，结算金额已扣除订单手续费及退款；所有对账时间均为{PLATFORM_TIME_ZONE_LABEL}。</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1180px] text-sm">
              <thead>
                <tr className="border-b border-slate-700/80 bg-slate-800/60">
                  <th className="text-left px-4 md:px-5 py-3 text-xs font-medium text-slate-500">结算批次</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">结算区间（UTC+8）</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-slate-500">订单数</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-slate-500">订单总额</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-slate-500">手续费</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-slate-500">退款</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-slate-500">净结算</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-slate-500">实际 U 价</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-slate-500">应付 USDT</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">状态</th>
                  <th className="text-left px-4 md:px-5 py-3 text-xs font-medium text-slate-500">完成时间</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {settlements.length === 0 && <tr><td colSpan={11} className="px-5 py-10 text-center text-slate-500">暂无{currency}后台结算记录</td></tr>}
                {settlements.map((item) => {
                  const sourceCurrency = (item.sourceCurrency || currency) as WithdrawCurrency;
                  return (
                    <tr key={item.id} className="tech-table-row transition-colors">
                      <td className="px-4 md:px-5 py-3">
                        <div className="font-mono text-xs text-emerald-300">{item.batchNo}</div>
                        <div className="mt-1 text-[11px] text-slate-600">{formatPlatformTime(item.createdAt, true)}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">{formatPlatformTime(item.periodStart, true)}<br />至 {formatPlatformTime(item.periodEnd, true)}</td>
                      <td className="px-4 py-3 text-right text-slate-300">{item.orderCount}</td>
                      <td className="px-4 py-3 text-right text-slate-300">{formatMoney(item.grossAmount, sourceCurrency)}</td>
                      <td className="px-4 py-3 text-right text-rose-300">-{formatMoney(item.feeAmount, sourceCurrency)}</td>
                      <td className="px-4 py-3 text-right text-rose-300">{formatMoney(item.refundAmount, sourceCurrency)}</td>
                      <td className="px-4 py-3 text-right font-medium text-slate-100">{formatMoney(item.netAmount, sourceCurrency)}</td>
                      <td className="px-4 py-3 text-right text-slate-300">{sourceCurrency === 'USDT' ? '-' : formatUsdtPrice(item.effectivePriceMicros)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="font-medium text-amber-300">{formatUsdtMinor(item.usdtAmountMinor)}</div>
                        {item.transferTxid && <div className="mt-1 max-w-[150px] truncate font-mono text-[11px] text-slate-500" title={item.transferTxid}>TXID: {item.transferTxid}</div>}
                      </td>
                      <td className="px-4 py-3">{settlementStatusBadge(item.status)}</td>
                      <td className="px-4 md:px-5 py-3 text-xs text-slate-500 whitespace-nowrap">{formatPlatformTime(item.completedAt, true)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="tech-card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-700/60">
            <h3 className="text-base font-semibold text-slate-100 flex items-center gap-2">
              <span className={`w-5 h-5 flex items-center justify-center ${isUsdt ? 'text-amber-400' : 'text-blue-400'}`}><i className="ri-history-line" /></span>
              {isUsdt ? 'USDT 提现记录' : '法币提现记录'}
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700/80 bg-slate-800/60">
                  <th className="text-left px-4 md:px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">提现单号</th>
                  <th className="text-left px-4 md:px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">金额</th>
                  <th className="text-left px-4 md:px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">到账账号</th>
                  <th className="text-left px-4 md:px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">状态</th>
                  <th className="text-left px-4 md:px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">申请时间</th>
                  <th className="text-left px-4 md:px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">到账时间</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {history.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">暂无{currency}提现记录</td></tr>}
                {history.map((h) => (
                  <tr key={h.id} className="tech-table-row transition-colors">
                    <td className="px-4 md:px-5 py-3 font-mono text-xs text-blue-300">{h.withdrawalNo}</td>
                    <td className="px-4 md:px-5 py-3 font-medium text-slate-200">{formatMoney(h.amount, (h.currency || currency) as WithdrawCurrency)}</td>
                    <td className="px-4 md:px-5 py-3 text-xs text-slate-400 max-w-[220px] truncate">{h.accountName} {h.accountNo}{h.extra ? ` · ${h.extra}` : ''}</td>
                    <td className="px-4 md:px-5 py-3">{statusBadge(h.status)}</td>
                    <td className="px-4 md:px-5 py-3 text-xs text-slate-500">{formatPlatformTime(h.createdAt)}</td>
                    <td className="px-4 md:px-5 py-3 text-xs text-slate-500">{formatPlatformTime(h.completedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function CurrencyButton({ label, icon, active, amber, onClick }: { label: string; icon: string; active: boolean; amber?: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border ${active ? (amber ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' : 'bg-blue-500/10 border-blue-500/30 text-blue-400') : 'border-slate-700/60 text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'}`}>
      <i className={icon} />
      {label}
    </button>
  );
}

function PreviewLine({ label, value }: { label: string; value: string }) {
  return <div className="flex items-center justify-between text-sm"><span className="text-slate-400">{label}</span><span className="text-slate-200 font-medium">{value}</span></div>;
}

function InfoPanel({ title, icon, amber, children }: { title: string; icon: string; amber?: boolean; children: React.ReactNode }) {
  return (
    <div className="tech-card p-5">
      <h4 className="text-sm font-semibold text-slate-200 mb-4 flex items-center gap-2">
        <span className={`w-4 h-4 flex items-center justify-center ${amber ? 'text-amber-400' : 'text-blue-400'}`}><i className={icon} /></span>
        {title}
      </h4>
      <div className="text-sm text-slate-400 space-y-3">{children}</div>
    </div>
  );
}

function RuleLine({ label, amber }: { label: string; amber?: boolean }) {
  return <div className="flex items-start gap-2"><span className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${amber ? 'bg-amber-500' : 'bg-blue-500'}`} /><span>{label}</span></div>;
}

function TimeLine({ left, right, green, muted }: { left: string; right: string; green?: boolean; muted?: boolean }) {
  return <div className="flex items-center justify-between gap-3"><span className="text-slate-400">{left}</span><span className={green ? 'text-green-400' : muted ? 'text-slate-400' : 'text-amber-400'}>{right}</span></div>;
}
